import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "./QAEscrowPayment.scss";

export default function QAEscrowPayment() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("CREDIT CARD");
  const [p2pPayment, setP2pPayment] = useState(null);
  const [auctionId, setAuctionId] = useState(location.state?.auctionId ?? null);

  const [summary, setSummary] = useState({
    inspectorName: "",
    inspectorRoleHub: "Lead Inspector",
    totalQuote: 0,
    currency: "USD",
  });

  const fromInspectionInitiated = Boolean(location.state?.fromInspectionInitiated);

  useEffect(() => {
    const state = location.state || {};
    if (!inspectionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchData = async () => {
      try {
        const inspectionRes = await CommonService.get(API_URLS.QA.INSPECTION_BY_ID(inspectionId));
        const inspection = inspectionRes?.data ?? null;
        if (cancelled || !inspection) {
          setLoading(false);
          return;
        }

        const paymentUx = inspection.p2p_payment ?? null;
        setP2pPayment(paymentUx);

        const paymentFlow = paymentUx?.payment_flow;
        const isAuctionP2p = paymentFlow === 'auction_p2p';
        if (isAuctionP2p && !fromInspectionInitiated) {
          navigate("/auction/qa/dashboard/inspections", { replace: true });
          return;
        }

        let inspectorName = "Quality Inspector";
        let inspectorRoleHub = "Lead Inspector";
        try {
          const profileRes = await CommonService.get(
            API_URLS.QA.INSPECTOR_PROFILE_BY_USER(inspection.inspector_user_id),
          );
          const profile = profileRes?.data ?? profileRes ?? {};
          inspectorName = profile.company_name || profile.name || inspectorName;
        } catch (_) {}

        const fee =
          state.totalQuote != null
            ? parseFloat(state.totalQuote)
            : parseFloat(inspection.payable_amount ?? inspection.inspection_fee) || 0;
        const currency = state.currency || inspection.currency || "USD";

        if (!cancelled) {
          setSummary({
            inspectorName: state.inspectorName || inspectorName,
            inspectorRoleHub: state.inspectorRoleHub || inspectorRoleHub,
            totalQuote: fee,
            currency,
          });
        }

        if (!auctionId) {
          try {
            const qaRequestRes = await CommonService.get(API_URLS.QA.GET_BY_ID(inspection.qa_request_id));
            if (qaRequestRes?.data?.auction_id) {
              setAuctionId(qaRequestRes.data.auction_id);
            }
          } catch (_) {}
        }
      } catch (err) {
        if (!cancelled) toast.error("Failed to load quotation summary");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [inspectionId, location.state, navigate, fromInspectionInitiated, auctionId]);

  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const isP2pFlow = p2pPayment?.is_p2p_linked === true;
  const canPay = !isP2pFlow || p2pPayment?.can_show_payment_now === true;
  const paymentApprovalPending = isP2pFlow && p2pPayment?.payment_approval_pending === true;

  const handlePayAndStart = async () => {
    if (!canPay) {
      toast.info("Payment is not available until internal payment approval is complete.");
      return;
    }
    setPaying(true);
    try {
      if (isP2pFlow) {
        await CommonService.post(API_URLS.QA.INSPECTION_MARK_PAID(inspectionId), {});
      } else {
        await new Promise((r) => setTimeout(r, 800));
      }
      if (fromInspectionInitiated && auctionId) {
        navigate(`/auction/${auctionId}/inspection-initiated`);
        toast.success("Payment completed.");
        return;
      }
      navigate(`/qa/inspection/${inspectionId}/payment/success`, {
        state: {
          auctionId,
          returnTo: p2pPayment?.payment_flow === 'quotation_p2p'
            ? '/buyer/qa-quotations-list'
            : '/auction/qa/dashboard/inspections',
        },
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Payment could not be completed. Please try again.",
      );
    } finally {
      setPaying(false);
    }
  };

  const handleBack = () => {
    if (fromInspectionInitiated && auctionId) {
      navigate(`/auction/${auctionId}/inspection-initiated`);
      return;
    }
    if (auctionId) {
      navigate(`/auction/${auctionId}/inspection-initiated`);
      return;
    }
    navigate(-1);
  };

  const paymentMethods = [
    { id: "CREDIT CARD", label: "Credit Card" },
    { id: "BANK WIRE", label: "Bank Wire" },
    { id: "CRYPTOCURRENCY", label: "Cryptocurrency" },
    { id: "PLATFORM CREDIT", label: "Platform Credit" },
  ];

  if (loading) {
    return (
      <div className="qa-escrow-page">
        <div className="qa-escrow-container">
          <div className="qa-escrow-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="qa-escrow-page">
      <div className="qa-escrow-container">
        <button type="button" className="qa-escrow-back" onClick={handleBack}>
          ← Back
        </button>

        {paymentApprovalPending ? (
          <div className="qa-escrow-approval-banner" role="status">
            Payment approval is in progress. Payment Now will be enabled once all approval levels are complete.
          </div>
        ) : null}

        <div className="qa-escrow-layout">
          <div className="qa-escrow-left">
            <h1 className="qa-escrow-title">ESCROW PAYMENT</h1>
            <p className="qa-escrow-subtitle">Secure funds for inspection service</p>

            <section className="qa-escrow-summary-card">
              <h2 className="qa-escrow-section-heading">Approved Quotation Summary</h2>
              <div className="qa-escrow-summary-inner">
                <div className="qa-escrow-inspector-info">
                  <div className="qa-escrow-avatar">
                    {summary.inspectorName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="qa-escrow-inspector-name">{summary.inspectorName}</div>
                    <div className="qa-escrow-inspector-role">{summary.inspectorRoleHub}</div>
                  </div>
                </div>
                <div className="qa-escrow-total-quote">
                  <span className="qa-escrow-total-label">Total Quote</span>
                  <span className="qa-escrow-total-amount">
                    {summary.currency === "USD" ? "$" : summary.currency + " "}
                    {formatAmount(summary.totalQuote)}
                  </span>
                </div>
              </div>
            </section>

            <section className="qa-escrow-methods">
              <h2 className="qa-escrow-section-heading">Select Payment Method</h2>
              <div className="qa-escrow-method-cards">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    className={`qa-escrow-method-card ${selectedMethod === method.id ? "active" : ""}`}
                    onClick={() => setSelectedMethod(method.id)}
                    disabled={!canPay}
                  >
                    <span className="qa-escrow-method-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                    </span>
                    <span>{method.label}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>

          <div className="qa-escrow-right">
            <div className="qa-escrow-protection-card">
              <div className="qa-escrow-protection-header">
                <span className="qa-escrow-shield-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </span>
                <h2 className="qa-escrow-protection-title">Escrow Protection</h2>
              </div>
              <p className="qa-escrow-protection-text">
                Funds are held securely by MarketTrust Escrow. Payment is only released to the
                inspector after you approve the final report.
              </p>
              <div className="qa-escrow-payable-row">
                <span className="qa-escrow-payable-label">Total Payable</span>
                <span className="qa-escrow-payable-amount">
                  {summary.currency === "USD" ? "$" : summary.currency + " "}
                  {formatAmount(summary.totalQuote)}
                </span>
              </div>
              {canPay ? (
                <button
                  type="button"
                  className="qa-escrow-pay-btn"
                  onClick={handlePayAndStart}
                  disabled={paying}
                >
                  {paying ? "Processing…" : isP2pFlow ? "Payment Now" : "Pay & Start"}
                </button>
              ) : (
                <p className="qa-escrow-pay-blocked">
                  {paymentApprovalPending
                    ? "Payment Now is unavailable while payment approval is pending."
                    : "Payment is not available yet."}
                </p>
              )}
            </div>

            <div className="qa-escrow-safeguards">
              <h2 className="qa-escrow-safeguards-title">Payment Safeguards</h2>
              <ul className="qa-escrow-safeguards-list">
                <li>
                  <span className="qa-escrow-check" aria-hidden>✓</span>
                  Full refund if inspector fails to attend site within the scheduled window.
                </li>
                <li>
                  <span className="qa-escrow-check" aria-hidden>✓</span>
                  Dispute resolution protocol active for non-conforming reports.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
